import {
  AMOUNT_WITHOUT_DECIMAL_PATTERN,
  COMMA_PATTERN,
} from '@/constants/patterns';

import { Tenant } from '@/types/common';

import { isCaymanTenant } from '@/utils/tenant';
import { isValidAmount } from '@/utils/validation';

const WHITESPACE_PATTERN = /\s/;
const LETTERS_PATTERN = /[a-zA-Z]/;
const DOT_PATTERN = /\./g;
const DIGITS_PATTERN = /[0-9]/;
const CAYMAN_FUNDING_INPUT_PATTERN = /^[0-9,]*(?:\.[0-9]*)?$/;

const FUNDING_AMOUNT_TRANSLATION_KEYS = {
  spaceNotAllowed:
    'questionDrawer.aboutFundingSource.fundingAmount.spaceNotAllowed',
  lettersNotAllowed:
    'questionDrawer.aboutFundingSource.fundingAmount.lettersNotAllowed',
  specialCharsNotAllowed:
    'questionDrawer.aboutFundingSource.fundingAmount.specialCharsNotAllowed',
  commasNotAllowed: 'questionDrawer.monthlyDeposit.monthlyDeposit.pattern',
  noDecimalAllowed:
    'questionDrawer.aboutFundingSource.fundingAmount.noDecimalAllowed',
} as const;

export const isValidFundingAmountCayman = (value: string): string | true => {
  if (!value) return true;

  if (WHITESPACE_PATTERN.test(value)) {
    return FUNDING_AMOUNT_TRANSLATION_KEYS.spaceNotAllowed;
  }

  if (LETTERS_PATTERN.test(value)) {
    return FUNDING_AMOUNT_TRANSLATION_KEYS.lettersNotAllowed;
  }

  if ((value.match(DOT_PATTERN) ?? []).length > 1) {
    return FUNDING_AMOUNT_TRANSLATION_KEYS.noDecimalAllowed;
  }

  if (
    !CAYMAN_FUNDING_INPUT_PATTERN.test(value) ||
    !DIGITS_PATTERN.test(value)
  ) {
    return value.includes('-')
      ? FUNDING_AMOUNT_TRANSLATION_KEYS.specialCharsNotAllowed
      : FUNDING_AMOUNT_TRANSLATION_KEYS.commasNotAllowed;
  }

  const withoutCommas = value.replace(COMMA_PATTERN, '');
  if (withoutCommas.includes('.')) {
    return FUNDING_AMOUNT_TRANSLATION_KEYS.noDecimalAllowed;
  }

  return true;
};

export const getFundingAmountValidationKey = (
  value: string,
  tenant: Tenant | undefined,
): string | true => {
  if (isCaymanTenant(tenant)) {
    return isValidFundingAmountCayman(value);
  }
  return isValidAmount(value, tenant);
};

export const formatFundingAmountCayman = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const withoutCommas = trimmed.replace(COMMA_PATTERN, '');

  if (withoutCommas.includes('.')) {
    const parsed = parseFloat(withoutCommas);
    if (Number.isNaN(parsed)) return null;
    return Math.round(parsed).toLocaleString('en-US');
  }

  if (AMOUNT_WITHOUT_DECIMAL_PATTERN.test(withoutCommas)) {
    return Number(withoutCommas).toLocaleString('en-US');
  }

  return null;
};

export const formatFundingAmountOnBlur = formatFundingAmountCayman;
